import { CloudWatchLogsClient, PutLogEventsCommand, CreateLogStreamCommand } from '@aws-sdk/client-cloudwatch-logs';
import { fetchAuthSession } from 'aws-amplify/auth';

class FrontendLogger {
  constructor() {
    this.client = null;
    this.logGroupName = process.env.REACT_APP_LOG_GROUP_NAME || '/aws/frontend/remote-assistant-site';
    this.logStreamName = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.sequenceToken = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    
    try {
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      if (!credentials) {
        console.warn('No credentials available for logging');
        return;
      }

      this.client = new CloudWatchLogsClient({
        region: process.env.REACT_APP_REGION || 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });

      await this.createLogStream();
      this.initialized = true;
    } catch (error) {
      console.warn('Failed to initialize logger:', error.message);
    }
  }

  async createLogStream() {
    try {
      await this.client.send(new CreateLogStreamCommand({
        logGroupName: this.logGroupName,
        logStreamName: this.logStreamName
      }));
    } catch (error) {
      if (error.name !== 'ResourceAlreadyExistsException') {
        console.warn('Failed to create log stream:', error.message);
      }
    }
  }

  async logEvent(eventType, data = {}) {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!this.client) {
      console.warn('Logger not initialized, skipping log event');
      return;
    }

    try {
      const logEvent = {
        timestamp: Date.now(),
        message: JSON.stringify({
          event_type: eventType,
          timestamp: new Date().toISOString(),
          ...data
        })
      };

      const command = new PutLogEventsCommand({
        logGroupName: this.logGroupName,
        logStreamName: this.logStreamName,
        logEvents: [logEvent],
        sequenceToken: this.sequenceToken
      });

      const response = await this.client.send(command);
      this.sequenceToken = response.nextSequenceToken;
    } catch (error) {
      console.warn('Failed to log event:', error.message);
    }
  }

  async logFrontendAuth(user) {
    await this.logEvent('frontend_auth', {
      cognito_user_id: user?.userId || user?.username,
      user_email: user?.attributes?.email || 'unknown'
    });
  }

  async logUserLogin(user) {
    await this.logEvent('user_login', {
      cognito_user_id: user?.userId || user?.username,
      user_email: user?.attributes?.email || 'unknown',
      login_method: 'cognito'
    });
  }

  async logUserLogout(user) {
    await this.logEvent('user_logout', {
      cognito_user_id: user?.userId || user?.username,
      user_email: user?.attributes?.email || 'unknown'
    });
  }

  async logOAuthCallback(user, provider = 'unknown') {
    await this.logEvent('oauth_callback', {
      cognito_user_id: user?.userId || user?.username,
      user_email: user?.attributes?.email || 'unknown',
      oauth_provider: provider
    });
  }
}

export default new FrontendLogger();